import { LockClosedIcon } from "@/components/match/lock-icons";
import { groupLabel } from "@/pages/settings/access-group-labels";
import { Switch } from "@/components/design/Switch";
import { useState } from "react";
import { useIntl } from "react-intl";
import { Dialog } from "@/components/common/Dialog";
import { Button } from "@/components/design/Button";

export interface PersonalShareSelection {
  ids: string[];
  allGroups?: { value: boolean; onChange: (value: boolean) => void };
  requiredIds?: readonly string[];
  recipients: readonly { id: string; name: string; eligible: boolean }[];
  onChange: (ids: string[]) => void;
  loading: boolean;
  error?: string;
  mixed?: boolean;
  sameAsBaseline?: boolean;
  displayCount?: number;
}

export function PersonalSharingPicker({ selection, id, publication, showRecipientNames = false, description, inline = false, countOnly = false, allGroups }: {
  selection: PersonalShareSelection;
  id?: string;
  showRecipientNames?: boolean;
  description?: string;
  inline?: boolean;
  countOnly?: boolean;
  allGroups?: { value: boolean; onChange: (value: boolean) => void };
  publication?: { value: boolean; mixed?: boolean; onChange: (value: boolean) => void };
}) {
  allGroups ??= selection.allGroups;
  const intl = useIntl();
  const t = (key: string) => intl.formatMessage({ id: key });
  const [open, setOpen] = useState(false);
  const eligible = selection.recipients.filter((group) => group.eligible);
  const retired = selection.recipients.filter((group) => !group.eligible && selection.ids.includes(group.id));
  const summary = selection.sameAsBaseline ? t("profile.sharing.sameAsBaseline") : publication?.mixed ? t("profile.sharing.mixedVisibility") : publication?.value ? t("profile.sharing.publiclyReadable")
    : selection.mixed ? t("profile.sharing.mixed") : selection.ids.length ? intl.formatMessage({ id: "profile.sharing.groupCount" }, { count: selection.ids.length })
    : t("common.share.off");
  const selectedGroups = selection.recipients.filter((group) => selection.ids.includes(group.id));
  const count = selection.displayCount ?? (publication?.value ? eligible.length : selection.ids.length);
  const publiclyReadable = publication?.value === true && !publication.mixed;
  const legacyPublic = !!allGroups && (publication?.value === true || publication?.mixed === true);
  const allGroupsSelected = allGroups?.value === true && !publication?.mixed;
  const allLabel = publiclyReadable ? t("profile.sharing.publiclyReadable") : allGroupsSelected ? t("profile.sharing.allGroups") : undefined;
  const labels = countOnly ? [{ key: "count", label: allLabel ? t("profile.sharing.allReaders") : String(count) }] : showRecipientNames && !publication?.value && !publication?.mixed && !selection.mixed
    && selectedGroups.length > 0 && selectedGroups.length === selection.ids.length
    ? selectedGroups.map((group) => ({ key: group.id, label: groupLabel(intl, group) }))
    : [{ key: "summary", label: summary }];
  const editor = (
      <div className={inline && allGroups ? "flex flex-wrap items-center gap-3" : "space-y-4"} role="presentation" onClick={(event) => event.stopPropagation()}>
        {description && <p className="text-smycles-meta text-ink-muted">{description}</p>}
        {allGroups && <div className="flex shrink-0 items-center gap-3">
          <span className="text-smycles-body">{t("profile.sharing.allGroups")}</span>
          <Switch id={id ? `${id}-all` : undefined} checked={allGroups.value} onChange={allGroups.onChange}
            ariaLabel={t("profile.sharing.allGroups")} disabled={selection.loading || !!selection.error || legacyPublic} />
        </div>}
        {legacyPublic && <div className="space-y-2">
          <p className="text-smycles-meta text-ink-muted">{t(publication?.mixed ? "profile.sharing.legacyPublicMixed" : "profile.sharing.legacyPublic")}</p>
          <Button id={id ? `${id}-restrict-to-groups` : undefined} variant="secondary" size="compact"
            disabled={selection.loading || !!selection.error} onClick={() => allGroups?.onChange(true)}>
            {t("profile.sharing.restrictToGroups")}
          </Button>
        </div>}
        {publication && !allGroups && <div className="space-y-2">
          <div className="flex items-center gap-3">
          <span className="text-smycles-body">{t("profile.sharing.publiclyReadable")}</span>
          <Switch id={id ? `${id}-public` : undefined} checked={publication.value} onChange={publication.onChange}
            ariaLabel={t("profile.sharing.publiclyReadable")} disabled={selection.loading || !!selection.error} />
          </div>
          {publication.value && <p className="text-smycles-meta text-ink-muted">{t("profile.sharing.publiclyReadableHint")}</p>}
        </div>}
        {!legacyPublic && !publication?.value && !allGroups?.value && <>
        <div id={id ? `${id}-groups` : undefined} className="flex flex-wrap gap-2" role="group"
          aria-label={t("profile.sharing.chooseGroups")}>
          {eligible.map((group) => {
            const selected = selection.ids.includes(group.id);
            const locked = selected && selection.requiredIds?.includes(group.id) === true;
            return <Button key={group.id} id={id ? `${id}-groups-option-${group.id}` : undefined} variant={selected ? "primary" : "secondary"} size="compact" className="gap-2"
              aria-disabled={locked || undefined} title={locked ? t("profile.sharing.requiredGroup") : undefined}
              aria-pressed={selected} disabled={selection.loading || !!selection.error || publication?.value === true}
              onClick={() => !locked && selection.onChange(selected
                ? selection.ids.filter((value) => value !== group.id)
                : [...selection.ids, group.id])}>
              {locked && <LockClosedIcon />}
              {groupLabel(intl, group)}
            </Button>;
          })}
        </div>
        {eligible.length === 0 && <p className="text-smycles-body text-ink-muted">{t("profile.sharing.noRecipients")}</p>}
        {retired.length > 0 && <div className="space-y-2">
          <p className="text-smycles-body text-ink-muted">{t("profile.sharing.retired")}</p>
          {retired.map((group) => <div key={group.id} className="flex items-center justify-between gap-2">
            <span className="text-smycles-body">{groupLabel(intl, group)}</span>
            <Button variant="ghost" size="sm" disabled={publication?.value === true || selection.requiredIds?.includes(group.id)} onClick={() => selection.onChange(selection.ids.filter((id) => id !== group.id))}>
              {t("common.remove")}
            </Button>
          </div>)}
        </div>}

        </>}
        {!inline && <Button id={id ? `${id}-close` : undefined} onClick={() => setOpen(false)}>{t("common.close")}</Button>}
      </div>
  );
  if (inline) return <div id={id}>{editor}</div>;
  return <span className="contents" onClick={(event) => event.stopPropagation()}>
    <div className="flex flex-wrap gap-2">
      {labels.map(({ key, label }, index) => <Button key={index === 0 ? "trigger" : key} id={index === 0 ? id : undefined}
        aria-label={countOnly ? (allLabel ?? intl.formatMessage({ id: "profile.sharing.groupCount" }, { count })) : undefined}
        title={countOnly ? (allLabel ?? (selection.mixed ? t("profile.sharing.mixed") : selectedGroups.map((group) => groupLabel(intl, group)).join(", ") || t("common.share.off"))) : undefined}
        variant={countOnly ? (publiclyReadable || allGroupsSelected || count > 0 ? "primary" : "secondary") : showRecipientNames ? (publication?.value || selection.ids.length ? "primary" : "secondary") : "ghost"}
        size={showRecipientNames ? "compact" : "sm"} disabled={selection.loading || !!selection.error}
        onClick={(event) => { event.stopPropagation(); setOpen(true); }}>{label}</Button>)}
    </div>
    <Dialog id={id ? `${id}-dialog` : undefined} open={open} onClose={() => setOpen(false)} title={t("profile.sharing.chooseGroups")}>
      {editor}
    </Dialog>
  </span>;
}
